import { Component, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  AssistantEvent,
  AssistantProposal,
  AssistantProviderKey,
  AssistantToolStep,
  AssistantTurn,
  AssistantUsage,
  totalInputTokens
} from '../../models/assistant.model';
import { AssistantService, AssistantHistoryMessage } from '../../services/assistant.service';

/**
 * Ask-a-question screen over the business's data.
 *
 * Most tool calls still only read. A handful can propose a write - a note, a reschedule, an
 * order accept/decline, an account's contact details - and every one of those renders as its
 * own card with Confirm/Reject buttons rather than as text: `apply()` below never lets a
 * `proposal` event turn into something claimed as done, and `confirmProposal()`/
 * `rejectProposal()` are the only places in this component that call something which changes
 * data, and only because a human clicked a button.
 *
 * The answer is a string and is rendered with `{{ }}`. It is never bound to `innerHTML` and
 * it must not be: tool results include supplier names, location notes and adjustment reasons
 * typed by people, so a `<script>` in a supplier record would come back through the model and
 * out into this page. Interpolation renders it as the characters it is.
 */
@Component({
    selector: 'app-assistant',
    templateUrl: './assistant.component.html',
    styleUrls: ['./assistant.component.scss'],
    standalone: false
})
export class AssistantComponent implements OnDestroy {

  /**
   * True when this is mounted inside the floating global launcher rather than the dedicated
   * /warehousing/assistant page. Only affects chrome (the page wrapper, the dashboard/reports
   * nav buttons) — the question/answer/streaming logic below is identical either way.
   */
  @Input() compact: boolean = false;

  /**
   * Mirrors the backend's own limits, which are enforced there and only echoed here.
   *
   * `assistant.controller.ts` rejects a question over 2000 characters and a history over 20
   * messages with a 400 before it opens the stream. Reproducing them means a long question is
   * caught by the counter under the box, and a long conversation is trimmed rather than
   * turning the twenty-first message into a failed answer with no obvious cause.
   */
  static readonly QUESTION_MAX: number = 2000;
  static readonly HISTORY_MAX: number = 20;

  /**
   * Per-message ceiling on replayed history. The backend rejects the whole request when any
   * one message exceeds 10000 characters, so a single long answer would otherwise sit in the
   * transcript and 400 every question after it — permanently, with "Clear transcript" the
   * only escape and nothing on screen saying so. Trimmed below the limit rather than at it,
   * so the marker appended below cannot push it back over.
   */
  static readonly HISTORY_CONTENT_MAX: number = 9800;

  questionMax: number = AssistantComponent.QUESTION_MAX;

  question: string = '';

  /** Both providers were asked for, so the choice is the operator's and it is per question. */
  provider: AssistantProviderKey = 'claude';

  providers: any[] = [
    { key: 'claude', name: 'Claude' },
    { key: 'openai', name: 'OpenAI' }
  ];

  /** Oldest first, so the page reads down like a conversation. */
  turns: AssistantTurn[] = [];

  /** Running cost for the session. Reset with the transcript, not per answer. */
  sessionUsage: AssistantUsage = new AssistantUsage();

  private subscription: Subscription = null;

  /** Questions people actually ask, so the first use of the box is not a blank stare. */
  examples: string[] = [
    'How much of each product is on hand at Bolton?',
    'Which locations are holding negative stock?',
    'What moved in and out of the yard last week?',
    'Which products have no cost basis recorded?'
  ];

  constructor(private assistantService: AssistantService) { }

  ngOnDestroy(): void {
    // Leaving the screen aborts the request in flight rather than letting it run on
    // against a component that is gone.
    this.cancelSubscription();
  }

  get streaming(): boolean {
    const turn: AssistantTurn = this.currentTurn;

    return turn ? turn.streaming : false;
  }

  /** The turn being answered, or the last one answered. Null before the first question. */
  get currentTurn(): AssistantTurn {
    return this.turns.length > 0 ? this.turns[this.turns.length - 1] : null;
  }

  get canAsk(): boolean {
    return !this.streaming && this.question.trim() !== '' && !this.questionTooLong;
  }

  get questionTooLong(): boolean {
    return this.question.length > AssistantComponent.QUESTION_MAX;
  }

  ask(): void {
    if (!this.canAsk) {
      return;
    }

    const asked: string = this.question.trim();

    // Built before the new turn is pushed, so the question being asked is not also in its
    // own history. Only answers that actually finished go up: replaying a truncated or
    // failed answer as context would have the model build on something it never said.
    const history: AssistantHistoryMessage[] = this.buildHistory();

    const turn: AssistantTurn = new AssistantTurn(asked, this.provider);
    turn.streaming = true;

    this.turns.push(turn);
    this.question = '';

    this.cancelSubscription();

    this.subscription = this.assistantService.ask(asked, history, this.provider)
      .subscribe({
        next: (event: AssistantEvent) => this.apply(turn, event),
        // The service turns every failure into an `error` event, so this arm is only reached
        // if that contract is ever broken. It still closes the turn, because the one outcome
        // this screen must not have is a spinner nobody can stop.
        error: (e: any) => {
          this.markFailed(turn, 'The assistant stopped unexpectedly.');
        },
        complete: () => {
          // A stream that completes without `done` and without `error` should not exist, but
          // if it does the turn is closed rather than left running.
          turn.streaming = false;
          turn.activeTool = '';
        }
      });
  }

  /** Stops the answer in flight and keeps what has arrived so far, marked incomplete. */
  stop(): void {
    const turn: AssistantTurn = this.currentTurn;

    this.cancelSubscription();

    if (turn && turn.streaming) {
      turn.streaming = false;
      turn.activeTool = '';
      turn.finished = false;
      turn.failed = true;
      turn.errorMessage = 'Stopped. This answer is incomplete.';
    }
  }

  clear(): void {
    this.cancelSubscription();

    this.turns = [];
    this.sessionUsage = new AssistantUsage();
  }

  useExample(example: string): void {
    if (this.streaming) {
      return;
    }

    this.question = example;
  }

  /** Enter sends, shift-Enter puts in a newline — a question can be more than one line. */
  onQuestionKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.ask();
    }
  }

  /**
   * The only place in this component that causes a write. Everything up to this point - the
   * model calling a propose_ tool, the card rendering - has changed nothing; this call is what
   * a human clicking Confirm actually does, and the backend re-validates ownership and expiry
   * before touching anything.
   */
  confirmProposal(proposal: AssistantProposal): void {
    if (proposal.confirming || !proposal.isPending) {
      return;
    }

    proposal.confirming = true;

    this.assistantService.confirmProposal(proposal.id)
      .pipe(take(1))
      .pipe(catchError(() => {
        proposal.confirming = false;
        proposal.status = 'failed';
        proposal.errorMessage = 'The connection to the assistant failed.';
        return of(null);
      }))
      .subscribe((outcome) => {
        proposal.confirming = false;

        if (!outcome) {
          return;
        }

        proposal.status = outcome.status;
        proposal.errorMessage = outcome.errorMessage || '';
      });
  }

  /** Marks a proposal rejected. Nothing is applied, on this screen or anywhere else. */
  rejectProposal(proposal: AssistantProposal): void {
    if (proposal.confirming || !proposal.isPending) {
      return;
    }

    proposal.confirming = true;

    this.assistantService.rejectProposal(proposal.id)
      .pipe(take(1))
      .pipe(catchError(() => {
        proposal.confirming = false;
        return of(null);
      }))
      .subscribe((outcome) => {
        proposal.confirming = false;
        proposal.status = outcome ? outcome.status : 'rejected';
      });
  }

  formatTokens(value: number): string {
    const amount: number = Number(value);

    if (!amount || isNaN(amount)) {
      return '0';
    }

    return amount.toLocaleString('en-GB');
  }

  /**
   * Total input for a turn — the full-rate portion plus the cached portion.
   *
   * The backend reports the two disjointly, so `inputTokens` alone is not "the input": on a
   * cached turn it is the smaller part of it. Printing it as the total put 503 beside a
   * parenthetical 14,958 "of them" cached, which is arithmetically impossible on its face.
   */
  totalInput(usage: AssistantUsage): number {
    return totalInputTokens(usage);
  }

  get hasSessionUsage(): boolean {
    return this.sessionUsage.inputTokens > 0
      || this.sessionUsage.cachedInputTokens > 0
      || this.sessionUsage.outputTokens > 0;
  }

  providerName(key: AssistantProviderKey): string {
    const match: any = this.providers.filter((p: any) => p.key === key)[0];

    return match ? match.name : key;
  }

  turnKey(index: number, turn: AssistantTurn): string {
    return index + '|' + turn.question;
  }

  /** Applies one frame to the turn it belongs to. */
  private apply(turn: AssistantTurn, event: AssistantEvent): void {
    if (event.type === 'text') {
      const text: string = event.text ? event.text : '';

      // Appended raw. No parsing, no markup, no sanitiser — the template renders the string.
      // The only structure applied is where one run ends and the next begins, and that comes
      // from the backend's own `tool` events rather than from anything inferred about the text.
      if (turn.breakBeforeNextText || turn.answerRuns.length === 0) {
        turn.answerRuns.push(text);
        turn.breakBeforeNextText = false;
        return;
      }

      turn.answerRuns[turn.answerRuns.length - 1] += text;
      return;
    }

    if (event.type === 'tool') {
      const name: string = event.name ? event.name : 'the data';

      turn.activeTool = name;
      turn.toolSteps.push(new AssistantToolStep(name));

      // Only break if there is something to break from. Several tools called back to back
      // would otherwise leave a blank paragraph between each pair.
      if (turn.answerRuns.length > 0) {
        turn.breakBeforeNextText = true;
      }
      return;
    }

    if (event.type === 'proposal') {
      turn.proposals.push(new AssistantProposal(event.proposal));

      // Same reasoning as a tool event: something just happened that the next text should not
      // run straight into.
      if (turn.answerRuns.length > 0) {
        turn.breakBeforeNextText = true;
      }
      return;
    }

    if (event.type === 'done') {
      turn.usage = this.readUsage(event.usage);
      turn.streaming = false;
      turn.finished = true;
      turn.activeTool = '';

      this.addToSessionUsage(turn.usage);
      return;
    }

    if (event.type === 'error') {
      this.markFailed(turn, event.message);
    }
  }

  private markFailed(turn: AssistantTurn, message: string): void {
    // The first thing to go wrong is the informative one, and a turn that already finished is
    // not failed at all. Both are held here rather than only in the service: the service is
    // what stops a second error being generated, this is what stops one overwriting the
    // first if it ever is. Losing "the provider returned 529: overloaded" and replacing it
    // with "the connection ended" turns something the operator can act on into a shrug.
    if (turn.finished || turn.failed) {
      return;
    }

    turn.streaming = false;
    turn.activeTool = '';
    turn.failed = true;
    turn.errorMessage = message ? message : 'The assistant could not answer that.';
  }

  private readUsage(usage: any): AssistantUsage {
    const counts: AssistantUsage = new AssistantUsage();

    if (!usage) {
      return counts;
    }

    counts.inputTokens = Number(usage.inputTokens) || 0;
    counts.cachedInputTokens = Number(usage.cachedInputTokens) || 0;
    counts.outputTokens = Number(usage.outputTokens) || 0;

    return counts;
  }

  private addToSessionUsage(usage: AssistantUsage): void {
    this.sessionUsage.inputTokens += usage.inputTokens;
    this.sessionUsage.cachedInputTokens += usage.cachedInputTokens;
    this.sessionUsage.outputTokens += usage.outputTokens;
  }

  private buildHistory(): AssistantHistoryMessage[] {
    const history: AssistantHistoryMessage[] = [];

    this.turns.forEach((turn: AssistantTurn) => {
      if (!turn.finished || turn.failed || !turn.hasAnswer()) {
        return;
      }

      history.push({ role: 'user', content: this.fitForHistory(turn.question) });
      history.push({ role: 'assistant', content: this.fitForHistory(turn.answerText()) });
    });

    // The backend rejects more than 20 messages outright, so the oldest are dropped here
    // instead. Trimmed in whole pairs from the front, because a history that starts with an
    // assistant message replying to a question that is no longer in it reads as the model
    // answering something nobody asked. The eleventh question keeps working; it simply
    // stops being able to refer back past the tenth.
    while (history.length > AssistantComponent.HISTORY_MAX) {
      history.splice(0, 2);
    }

    return history;
  }

  /**
   * Shortens one replayed message to something the backend will accept.
   *
   * Shortened rather than dropped: dropping the turn would leave a follow-up like "and the
   * other depot?" with nothing to refer back to, and the model would answer confidently
   * against a gap. The marker is there so it is told the text was cut rather than being
   * handed a sentence that stops mid-word as if that were all it said.
   *
   * This only affects what is sent back as context. The transcript on screen keeps the whole
   * answer — the operator reads the real thing.
   */
  private fitForHistory(content: string): string {
    const text: string = content ? content : '';

    if (text.length <= AssistantComponent.HISTORY_CONTENT_MAX) {
      return text;
    }

    // Kept from the front. The opening of an answer carries the figures and the caveats; the
    // tail is usually the closing advice, which is the part a follow-up needs least.
    return text.substring(0, AssistantComponent.HISTORY_CONTENT_MAX)
      + '\n[earlier answer shortened for length]';
  }

  private cancelSubscription(): void {
    if (this.subscription) {
      // Unsubscribing aborts the fetch, which is what stops the request rather than just
      // stopping this screen listening to it.
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }
}
